import React from 'react';
import './Summary.css'
import { useTranslation } from 'react-i18next';
import { getSummaryDataState } from '../../redux/reducers/getSummaryData/summaryDataReducer';
import { useSelector } from 'react-redux';
import { ProfileAttributes } from '../../types/profile/ProfileAttributes';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import AccordionItem, { DefaultAccordionProviderOptions } from '../../components/common/AccordionItem/AccordionItem'
import { commaSeparate } from '../../utils/formatter';
import { selectSummaryData } from '../../redux/selectors/exportSelectorPaths';

export default function PropertySummary(): JSX.Element {
    const propertyOccupiedRow = ["officeOccupiedArea", "retailOccupiedArea", "warehouseStorageOccupiedArea", "manufactureOtherIndustrialUseOccupiedArea"]
    const propertyVacantRow = ["officeVacantArea", "retailVacantArea", "warehouseStorageVacantArea", "manufactureOtherIndustrialUseVacantArea"]
    const { t } = useTranslation(['parking_property', 'summary']);
    const rowTranslationKey = ['propertyTableHeaderOffice', 'propertyTableHeaderRetail', 'propertyTableHeaderWarehouseOther', 'propertyTableHeaderManufacturing'];
    const summaryData: getSummaryDataState = useSelector(selectSummaryData);
    const propertyData = (summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)

    const calculateTotalArea = (type: 'vacant' | 'occupied' | 'all') => {
        let areaTotal = null;
        for (let i = 0; i < rowTranslationKey.length; i++) {
            let total = 0;
            if (type === 'all' || type === 'vacant') {
                total += (propertyData[propertyVacantRow[i] as keyof typeof propertyData] ? propertyData[propertyVacantRow[i] as keyof typeof propertyData] as number : 0);
            }
            if (type === 'all' || type === 'occupied') {
                total += (propertyData[propertyOccupiedRow[i] as keyof typeof propertyData] ? propertyData[propertyOccupiedRow[i] as keyof typeof propertyData] as number : 0);
            }
            if ((type === 'all' && (propertyData[propertyVacantRow[i] as keyof typeof propertyData] || propertyData[propertyOccupiedRow[i] as keyof typeof propertyData]))
                || (type === 'vacant' && propertyData[propertyVacantRow[i] as keyof typeof propertyData]) || (type === "occupied" && propertyData[propertyOccupiedRow[i] as keyof typeof propertyData])) {
                areaTotal = (areaTotal ?? 0) + total;
            }
        }
        return commaSeparate(areaTotal)
    }

    return (
        <>
            {summaryData.data && ("tenant_summary" in (summaryData.data as Record<string, unknown>)) && (summaryData.data as Record<string, unknown>)["tenant_summary"] &&
                ((summaryData.data as Record<string, unknown>)["tenant_summary"] as Record<string, unknown>[]).length > 0 && <ControlledAccordion providerValue={providerValue}>
                    <AccordionItem header={t("area", { ns: 'summary' })}
                        key="propertySummary"
                        itemKey="propertySummary"
                        headingLevel={2}>
                        <div className="summary-grid">
                            <div className='summary-section-container'>
                                <table className='summary-table' role='presentation'>
                                    <thead>
                                        <tr>
                                            <th>{t("parkingTableTotal", { ns: 'parking_property' }) + " " + t("area", { ns: 'parking_property' }) + " " + t("propertyTableHeaderSpaceType2", { ns: 'parking_property' })}</th>
                                            <th>{calculateTotalArea('all')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {propertyVacantRow.map((field, index) => {
                                            const total = ((propertyData[field as keyof typeof propertyData] ? propertyData[field as keyof typeof propertyData] as number : 0)
                                                + (propertyData[propertyOccupiedRow[index] as keyof typeof propertyData] ? propertyData[propertyOccupiedRow[index] as keyof typeof propertyData] as number : 0));
                                            return <tr key={`row-total-${index}`}>
                                                <td>
                                                    {(t(rowTranslationKey[index], { ns: 'parking_property' })).replaceAll(/([^\s])(\/)/g, "$1 $2").replaceAll(/(\/)([^\s])/g, "$1 $2")}
                                                </td>
                                                <td>
                                                    {commaSeparate(propertyData[field as keyof typeof propertyData] || propertyData[propertyOccupiedRow[index] as keyof typeof propertyData] ? total : "")}
                                                </td>
                                            </tr>
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className='summary-section-container'>
                                <table className='summary-table' role='presentation'>
                                    <thead>
                                        <tr>
                                            <th>{t("parkingTableTotal", { ns: 'parking_property' }) + " " + t("propertyTableRowOccupied", { ns: 'parking_property' }) + " " + t("propertyTableHeaderSpaceType2", { ns: 'parking_property' })}</th>
                                            <td>{calculateTotalArea('occupied')}</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {propertyOccupiedRow.map((field, index) => (
                                            <tr key={`row-occupied-${index}`}>
                                                <td>
                                                    {(t(rowTranslationKey[index], { ns: 'parking_property' })).replaceAll(/([^\s])(\/)/g, "$1 $2").replaceAll(/(\/)([^\s])/g, "$1 $2")}
                                                </td>
                                                <td>
                                                    {commaSeparate(propertyData[field as keyof typeof propertyData] ? propertyData[field as keyof typeof propertyData] as number : "")}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className='summary-section-container'>
                                <table className='summary-table' role='presentation'>
                                    <thead>
                                        <tr>
                                            <th>{t("parkingTableTotal", { ns: 'parking_property' }) + " " + t("propertyTableRowVacant", { ns: 'parking_property' }) + " " + t("propertyTableHeaderSpaceType2", { ns: 'parking_property' })}</th>
                                            <td>{calculateTotalArea('vacant')}</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {propertyVacantRow.map((field, index) => (
                                            <tr key={`row-vacant-${index}`}>
                                                <td>
                                                    {(t(rowTranslationKey[index], { ns: 'parking_property' })).replaceAll(/([^\s])(\/)/g, "$1 $2").replaceAll(/(\/)([^\s])/g, "$1 $2")}
                                                </td>
                                                <td>
                                                    {commaSeparate(propertyData[field as keyof typeof propertyData] ? propertyData[field as keyof typeof propertyData] as number : "")}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </AccordionItem>
                </ControlledAccordion>
            }
        </>
    )
}